import React, { useState, useEffect } from 'react';
import { auth, db } from '../../config/firebase.js';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { deleteUser, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import DeleteAccountSection from './DeleteAccountSection.js';
import NotificationPreferences from './NotificationPreferences.js';
import { useUser } from '../../utils/UserContext';
import GoogleAnalytics from '../../analyticsComponents/GoogleAnalytics.js';
import logger from '../../utils/logger';

const ViewAccountPage = () => {
  const [userData, setUserData] = useState({
    full_name: '',
    email: '',
    institution: '',
    age: '',
    role: '',
    requested_role: '',
  });

  const [editMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [reauthPassword, setReauthPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const navigate = useNavigate();
  const { currentUser, profile, role } = useUser();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return navigate('/sign-in');

        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists()) throw new Error('User data not found');

        setUserData((prev) => ({
          ...prev,
          ...userDoc.data(),
        }));
      } catch (error) {
        logger.error('Error fetching user data:', error);
        setErrorMessage('Failed to load user data.');
      }
    };

    fetchUser();
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setErrorMessage('');

    try {
      const user = auth.currentUser;
      if (!user) return navigate('/sign-in');

      const updates = {
        full_name: userData.full_name || null,
        institution: userData.institution || null,
        age: userData.age || null,
        requested_role: userData.requested_role || null,
      };

      await updateDoc(doc(db, 'users', user.uid), updates);
      setEditMode(false);
    } catch (error) {
      logger.error('Update error:', error);
      setErrorMessage('Error saving changes.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const credential = EmailAuthProvider.credential(user.email, reauthPassword);
      await reauthenticateWithCredential(user, credential);
      await deleteDoc(doc(db, 'users', user.uid));
      await deleteUser(user);

      navigate('/');
    } catch (error) {
      logger.error('Delete error:', error);
      setErrorMessage(
        error.code === 'auth/wrong-password'
          ? 'Incorrect password.'
          : 'Could not delete account.'
      );
    }
  };

  const fields = [
    { label: 'Full Name', name: 'full_name', value: userData.full_name, editable: true, icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
    )},
    { label: 'Email', value: userData.email, editable: false, icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
    )},
    { label: 'Institution', name: 'institution', value: userData.institution, editable: true, icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
    )},
    { label: 'Age', name: 'age', value: userData.age, editable: true, type: 'number', icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
    )},
    { label: 'Role', value: userData.role, editable: false, icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
    )},
    {
      label: 'Requested Role',
      name: 'requested_role',
      value: userData.requested_role,
      editable: true,
      options: ['', 'student', 'educator'],
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
      ),
    },
  ];

  return (
    <div className="min-h-screen w-full bg-gray-50 pb-16">
      <GoogleAnalytics page="ViewAccountPage" eventType="viewAccount_page" />

      {/* Page header */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-10 pb-2">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">My Account</h1>
        <p className="mt-1 text-sm text-gray-500">Manage your personal details and preferences.</p>
      </div>

      {/* Profile card */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 mt-6">
        {errorMessage && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            {errorMessage}
          </div>
        )}

        {/* Avatar + name header */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-brand-600 flex items-center justify-center text-white text-lg font-bold shrink-0">
              {userData.full_name ? userData.full_name.charAt(0).toUpperCase() : '?'}
            </div>
            <div className="min-w-0">
              <p className="text-base font-semibold text-gray-900 truncate">{userData.full_name || 'No name set'}</p>
              <p className="text-sm text-gray-500 truncate">{userData.email}</p>
            </div>
            <div className="ml-auto">
              {!editMode && (
                <button
                  onClick={() => setEditMode(true)}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                  Edit
                </button>
              )}
            </div>
          </div>

          {/* Fields */}
          <div className="divide-y divide-gray-100">
            {fields.map((field, idx) => (
              <div key={idx} className="px-6 py-4 flex items-start sm:items-center gap-4 flex-col sm:flex-row">
                <div className="flex items-center gap-2.5 sm:w-40 shrink-0">
                  <span className="text-gray-400">{field.icon}</span>
                  <span className="text-sm font-medium text-gray-500">{field.label}</span>
                </div>
                <div className="flex-1 w-full">
                  {field.editable && editMode ? (
                    field.options ? (
                      <select
                        name={field.name}
                        value={field.value}
                        onChange={handleInputChange}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:outline-none transition-shadow"
                      >
                        {field.options.map((option, i) => (
                          <option key={i} value={option}>
                            {option ? option.charAt(0).toUpperCase() + option.slice(1) : 'Select Role'}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type || 'text'}
                        name={field.name}
                        value={field.value || ''}
                        onChange={handleInputChange}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:outline-none transition-shadow"
                      />
                    )
                  ) : (
                    <span className="text-sm text-gray-900">{field.value || <span className="text-gray-400">Not set</span>}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Save / Cancel bar (only in edit mode) */}
          {editMode && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                onClick={() => setEditMode(false)}
                disabled={isSaving}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${
                  isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-brand-600 hover:bg-brand-700'
                }`}
              >
                {isSaving && (
                  <svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                )}
                {isSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>

        {(role === 'moderator' || role === 'admin') && currentUser && (
          <NotificationPreferences uid={currentUser.uid} profile={profile} role={role} />
        )}

        {/* Danger zone */}
        <div className="mt-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3 px-0.5">Danger Zone</p>
          <DeleteAccountSection
            deleteConfirm={deleteConfirm}
            reauthPassword={reauthPassword}
            onDelete={handleDeleteAccount}
            onCancel={() => setDeleteConfirm(false)}
            onConfirmStart={() => setDeleteConfirm(true)}
            onPasswordChange={(e) => setReauthPassword(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default ViewAccountPage;
